import { useState, useEffect, useCallback } from "react";
import { Cookie, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const GA_MEASUREMENT_ID = "G-352GMDCF8V";
const CONSENT_KEY = "auli_cookie_consent";

type ConsentStatus = "accepted" | "declined" | null;

/**
 * Loads the Google Analytics script dynamically after user consent.
 * LGPD-compliant: GA only loads when the user explicitly accepts.
 */
const loadGoogleAnalytics = () => {
  // Prevent double-loading
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return;
  }

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Configure GA after script loads
  script.onload = () => {
    if (typeof window.gtag === "function") {
      window.gtag("js", new Date());
      window.gtag("config", GA_MEASUREMENT_ID);
    }
  };
};

/**
 * CookieConsentBanner - LGPD Compliance
 * 
 * Blocks Google Analytics until the user provides explicit consent.
 * Consent is persisted in localStorage.
 */
export const CookieConsentBanner = () => {
  const [consent, setConsent] = useState<ConsentStatus>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus;

    if (stored === "accepted") {
      setConsent("accepted");
      loadGoogleAnalytics();
    } else if (stored === "declined") {
      setConsent("declined");
    } else {
      // No consent stored: show the banner after a brief delay
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setConsent("accepted");
    setIsVisible(false);
    loadGoogleAnalytics();
  }, []);

  const handleDecline = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "declined");
    setConsent("declined");
    setIsVisible(false);
  }, []);

  // Don't render if consent already given
  if (consent !== null) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-2 sm:p-3"
        >
          <div className="mx-auto w-full max-w-3xl rounded-2xl border border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl">
            <div className="flex flex-col gap-3 p-3 lg:flex-row lg:items-center lg:gap-5 lg:p-4">
              {/* Icon + Text */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-xl bg-[hsl(var(--auli-teal))]/10 flex items-center justify-center">
                  <Cookie className="h-5 w-5 text-[hsl(var(--auli-teal))]" />
                </div>
                <div className="space-y-1 min-w-0">
                  <p className="font-heading text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <ShieldCheck className="h-4 w-4 text-[hsl(var(--auli-teal))]" />
                    Sua privacidade importa
                  </p>
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                    Usamos cookies de análise para melhorar sua experiência. Seus dados nunca são vendidos.{" "}
                    <Link
                      to="/politica-de-privacidade"
                      className="text-[hsl(var(--auli-teal))] hover:underline font-medium"
                    >
                      Política de Privacidade
                    </Link>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2 sm:gap-3 ml-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDecline}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Recusar
                </Button>
                <Button
                  size="sm"
                  onClick={handleAccept}
                  className="bg-[hsl(var(--auli-teal))] hover:bg-[hsl(var(--auli-teal))]/90 text-white font-semibold px-6"
                >
                  Aceitar
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
